import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { StyledToast, StyledToastContainer, ToastIcon } from './styled';
import { CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined, WarningOutlined, CloseOutlined } from '@ant-design/icons';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
    id: string;
    type: ToastType;
    message: string;
    description?: string;
    duration?: number;
}

interface ToastItemProps {
    toast: Toast;
    onClose: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onClose }) => {
    const [isExiting, setIsExiting] = useState(false);

    useEffect(() => {
        const duration = toast.duration || 4000;
        const timer = setTimeout(() => {
            setIsExiting(true);
            setTimeout(() => onClose(toast.id), 300);
        }, duration);

        return () => clearTimeout(timer);
    }, [toast, onClose]);

    const getIcon = () => {
        switch (toast.type) {
            case 'success':
                return <CheckCircleOutlined />;
            case 'error':
                return <CloseCircleOutlined />;
            case 'warning':
                return <WarningOutlined />;
            case 'info':
                return <InfoCircleOutlined />;
        }
    };

    return (
        <StyledToast type={toast.type} $isExiting={isExiting}>
            <ToastIcon type={toast.type}>
                {getIcon()}
            </ToastIcon>
            <div className="toast-content">
                <div className="toast-message">{toast.message}</div>
                {toast.description && (
                    <div className="toast-description">{toast.description}</div>
                )}
            </div>
            <button
                className="toast-close"
                onClick={() => {
                    setIsExiting(true);
                    setTimeout(() => onClose(toast.id), 300);
                }}
            >
                <CloseOutlined />
            </button>
        </StyledToast>
    );
};

interface ToastContainerProps {
    toasts: Toast[];
    onClose: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onClose }) => {
    return createPortal(
        <StyledToastContainer>
            {toasts.map(toast => (
                <ToastItem key={toast.id} toast={toast} onClose={onClose} />
            ))}
        </StyledToastContainer>,
        document.body
    );
};

// Toast manager
class ToastManager {
    private toasts: Toast[] = [];
    private listeners: Set<(toasts: Toast[]) => void> = new Set();

    subscribe(listener: (toasts: Toast[]) => void) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    private notify() {
        this.listeners.forEach(listener => listener([...this.toasts]));
    }

    show(type: ToastType, message: string, description?: string, duration?: number) {
        const id = `toast-${Date.now()}-${Math.random()}`;
        const toast: Toast = { id, type, message, description, duration };
        this.toasts.push(toast);
        this.notify();
        return id;
    }

    success(message: string, description?: string, duration?: number) {
        return this.show('success', message, description, duration);
    }

    error(message: string, description?: string, duration?: number) {
        return this.show('error', message, description, duration);
    }

    warning(message: string, description?: string, duration?: number) {
        return this.show('warning', message, description, duration);
    }

    info(message: string, description?: string, duration?: number) {
        return this.show('info', message, description, duration);
    }

    close(id: string) {
        this.toasts = this.toasts.filter(t => t.id !== id);
        this.notify();
    }

    closeAll() {
        this.toasts = [];
        this.notify();
    }
}

export const toast = new ToastManager();

// React hook for toast
export const useToast = () => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    useEffect(() => {
        const unsubscribe = toast.subscribe(setToasts);
        return () => {
            unsubscribe();
        };
    }, []);

    return {
        toasts,
        close: (id: string) => toast.close(id),
    };
};
